import { useCallback, useRef } from "react"

interface UseTweetFeedStateArgs {
	results: Array<{ tweetId: string }>
	status: string
	isLoading: boolean
	loadMore: (count: number) => void
	loadMoreCount: number
	/** When false, treat as "still waiting" even if status isn't loading (e.g. weekId not yet known) */
	isQueryReady?: boolean
}

export function useTweetFeedState({
	results,
	status,
	isLoading,
	loadMore,
	loadMoreCount,
	isQueryReady = true,
}: UseTweetFeedStateArgs) {
	// Track if we've ever loaded data — avoid showing empty state during sort switch
	const hasLoadedOnce = useRef(false)
	if (results.length > 0) {
		hasLoadedOnce.current = true
	}

	// Preserve container height during sort switch to prevent layout shift
	const contentRef = useRef<HTMLDivElement>(null)
	const frozenHeight = useRef<number | null>(null)

	// Clear frozen height once new results arrive
	if (results.length > 0 && frozenHeight.current !== null) {
		frozenHeight.current = null
	}

	const captureFrozenHeight = useCallback(() => {
		if (contentRef.current) {
			frozenHeight.current = contentRef.current.offsetHeight
		}
	}, [])

	const handleLoadMore = useCallback(() => {
		loadMore(loadMoreCount)
	}, [loadMore, loadMoreCount])

	const isSwitchingSort = hasLoadedOnce.current && status === "LoadingFirstPage"
	const isLoadingMore = status === "LoadingMore"
	// Still waiting for first data
	const isWaiting =
		results.length === 0 &&
		!hasLoadedOnce.current &&
		(!isQueryReady || isLoading || status === "LoadingFirstPage")
	// Truly empty — loaded but no tweets
	const isEmpty = results.length === 0 && !isSwitchingSort && !isWaiting

	return {
		contentRef,
		frozenHeight: frozenHeight.current,
		captureFrozenHeight,
		handleLoadMore,
		hasLoadedOnce: hasLoadedOnce.current,
		isSwitchingSort,
		isLoadingMore,
		isWaiting,
		isEmpty,
	}
}
